import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useRecentlyViewed } from '../hooks/useRecentlyViewed'

interface Product {
  id: string
  name: string
  price: number
  image_urls: string[]
}

interface RecentlyViewedProps {
  excludeProductId?: string
  title?: string
}

export default function RecentlyViewed({ excludeProductId, title = 'Recently Viewed' }: RecentlyViewedProps) {
  const { recentlyViewedIds, loading: idsLoading, getRecentlyViewedExcluding } = useRecentlyViewed()
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)

  // Get the IDs to display (excluding current product if on product page)
  // Memoize to prevent new array reference on every render (which would cause infinite loop)
  const displayIds = useMemo(() => {
    return excludeProductId
      ? getRecentlyViewedExcluding(excludeProductId)
      : recentlyViewedIds
  }, [excludeProductId, recentlyViewedIds, getRecentlyViewedExcluding])

  useEffect(() => {
    if (idsLoading || displayIds.length === 0) {
      setProducts([])
      setLoading(false)
      return
    }

    const fetchProducts = async () => {
      setLoading(true)
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

        // Fetch all products that match our IDs
        const idsParam = displayIds.map(id => `"${id}"`).join(',')
        const response = await fetch(
          `${supabaseUrl}/rest/v1/products?id=in.(${idsParam})&select=id,name,price,image_urls`,
          {
            method: 'GET',
            headers: {
              'apikey': supabaseKey,
              'Authorization': `Bearer ${supabaseKey}`,
              'Content-Type': 'application/json',
            },
            signal: AbortSignal.timeout(10000)
          }
        )

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data: Product[] = await response.json()

        // Sort products to match the order of displayIds (most recent first)
        // Also filters out any products that no longer exist
        const productMap = new Map(data.map(p => [p.id, p]))
        const sortedProducts = displayIds
          .map(id => productMap.get(id))
          .filter((p): p is Product => p !== undefined)

        setProducts(sortedProducts)
      } catch (error) {
        console.error('Error fetching recently viewed products:', error)
        setProducts([])
      } finally {
        setLoading(false)
      }
    }

    fetchProducts()
  }, [displayIds, idsLoading])

  // Don't render anything if there are no products to show
  if (idsLoading || loading) {
    return null
  }

  if (products.length === 0) {
    return null
  }

  return (
    <div className="mt-12">
      <h2 className="text-xl font-bold text-gray-900 mb-5">{title}</h2>

      {/* Horizontal scroll container */}
      <div className="overflow-x-auto pb-4 -mx-2 px-2">
        <div className="flex gap-4" style={{ minWidth: 'min-content' }}>
          {products.map((product) => (
            <Link
              key={product.id}
              to={`/products/${product.id}`}
              className="flex-shrink-0 w-40 sm:w-48 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden group"
            >
              {/* Product Image */}
              <div className="aspect-square bg-gray-50 flex items-center justify-center p-3">
                {product.image_urls && product.image_urls.length > 0 ? (
                  <img
                    src={product.image_urls[0]}
                    alt={product.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1}
                      stroke="currentColor"
                      className="w-8 h-8 text-gray-300"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                      />
                    </svg>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="p-3">
                <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-1 group-hover:text-purple-600 transition-colors">
                  {product.name}
                </h3>
                <p className="text-sm font-bold text-gray-900">
                  ${product.price.toFixed(2)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
